import { CheckCircleOutline, Error, ReceiptLong, ReportProblem } from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { fiscalize } from '../../../api/order/fiscalize';
import { Order, PrroReceipt } from '../../../apiTypes';
import Loader from '../../../components/Loader';
import PrimaryButton from '../../../components/PrimaryButton';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import getBillCalc, { BillCalc } from '../../../utils/getBillCalc';
import useMedia from '../../../utils/useMedia';
import PrroReceiptPrintModal from './PrroReceiptPrintModal';
import usePrroReceiptStatus from './usePrroReceiptStatus';

type Props = {
  order: DbDocument<Order>;
};

type PrroReceiptBlockProps = {
  order: DbDocument<Order>;
  legalEntityId: string;
  receipt: PrroReceipt;
  billCalc: BillCalc;
};

const PrroReceiptBlock = ({ legalEntityId, receipt, order, billCalc }: PrroReceiptBlockProps) => {
  const { isSm } = useMedia();
  const { legalEntitiesMap, dishesMap, legalEntities } = useCafe();
  const { cafeSlug } = useAdmin();
  const legalEntity = legalEntitiesMap[legalEntityId];
  const cashierToken = legalEntity?.prroCheckBox.cashierToken;
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  usePrroReceiptStatus({ orderId: order.id, legalEntity, receipt, setError });

  const method: 'cash' | 'card' = useMemo(
    () => (Object.values(order.paid?.byCard || {}).some((i) => i > 0) ? 'card' : 'cash'),
    [order.paid?.byCard]
  );

  const handleFiscalize = useCallback(async () => {
    if (legalEntity) {
      setLoading(true);

      const { error } = await fiscalize({
        order,
        billCalc,
        cafeSlug,
        method,
        dishesMap,
        legalEntity,
        defaultLegalEntityId: legalEntities[0]?.id,
        service: order.paid?.service || 0,
        discount: order.paid?.discount || 0,
      });

      if (error) {
        setError(error);
      }

      setLoading(false);
    }
  }, [billCalc, cafeSlug, dishesMap, legalEntities, legalEntity, method, order]);

  return (
    <Box
      sx={{
        mb: isSm ? 1 : 3,
        borderRadius: 1,
        px: 2,
        py: 1,
      }}
    >
      {isSm && <Divider sx={{ mb: 2 }} />}

      <Typography sx={{ mb: 2 }} variant="caption">
        {legalEntity?.name}
      </Typography>

      {(loading || (receipt.id && receipt.status === 'waiting')) && (
        <Box sx={{ display: 'flex' }}>
          <Loader open />
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Error color="error" />
          <Typography>{error}</Typography>
        </Box>
      )}

      {!loading && !receipt.id && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ReportProblem color="disabled" />
            <Typography>Не фіскалізовано</Typography>
          </Box>
          <PrimaryButton variant="contained" onClick={handleFiscalize} sx={{ ml: 2 }}>
            Фіскалізувати чек
          </PrimaryButton>
        </Box>
      )}

      {!loading && receipt.id && receipt.status === 'complete' && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <CheckCircleOutline color="success" />
            <Typography>Фіскалізовано</Typography>
          </Box>

          {cashierToken && receipt.id && (
            <PrimaryButton
              color="secondary"
              variant="contained"
              startIcon={<ReceiptLong />}
              onClick={() => setOpen(true)}
            >
              Фіскальний чек
            </PrimaryButton>
          )}
        </Box>
      )}

      {open && cashierToken && receipt.id && (
        <PrroReceiptPrintModal receiptId={receipt.id} cashierToken={cashierToken} onClose={() => setOpen(false)} />
      )}
    </Box>
  );
};

const PrroReceipts = ({ order }: Props) => {
  const { legalEntities } = useCafe();
  const { isSm } = useMedia();

  const orderItems = useMemo(() => Object.values(order.items), [order.items]);

  const billCalc = useMemo(
    () =>
      getBillCalc({
        orderItems,
        discount: order.paid?.discount || 0,
        service: order.paid?.service || 0,
        promotionsApplied: order.paid?.promotionsApplied || [],
        legalEntities,
      }),
    [legalEntities, order.paid?.discount, order.paid?.promotionsApplied, order.paid?.service, orderItems]
  );

  if (!order.prroReceipts || Object.keys(order.prroReceipts).length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 0 },
        borderRadius: 1,
        bgcolor: (t) => (isSm ? `${t.palette.secondary.light}22` : undefined),
      }}
    >
      <Typography variant="h6" sx={{ mb: 1, ml: 2, color: (t) => t.palette.text.secondary }}>
        ПРРО
      </Typography>

      {Object.entries(order.prroReceipts).map(([legalEntityId, receipt]) => (
        <Fragment key={legalEntityId}>
          <PrroReceiptBlock legalEntityId={legalEntityId} receipt={receipt} order={order} billCalc={billCalc} />
        </Fragment>
      ))}
    </Box>
  );
};

export default PrroReceipts;
